// showCreatedMaterial — the agent SHOWS what it made (owner, 2026-10-01).
//
// When a live turn's material build settles (`build_settled`: the create tool
// committed a new material), the right panel opens it once, so the chat reply
// can stay a short summary instead of reciting the material. The right panel is
// "where the agent shows the entity" (right-panel focus model), so this asks the
// frame for `show-in-panel`; the material module follows the panel focus and
// leads with the generation side view when the material has a skeleton/job.
// The same `show-in-panel` every chat reference uses (a click on a chip).
//
// Why `build_settled` and not the completed tool step's `resource_touched`
// detail: it is the precise fact. Only the create tool publishes it, it is a
// transient LIVE-only signal (never replayed by a transcript reload, so old
// turns never re-open anything), and it carries the material's id + title
// without a tool-name lookup against the transcript.
//
// Guards, all pure reads of the Model: only the conversation in view, and only
// once per build (a re-delivered `build_settled` for a card already settled, or
// already replaced by the reloaded durable material entry, is dropped).

import type { Signal } from "../signal.js";
import type { TurnProgressed } from "./command.js";
import { chatSessionFor } from "./model.js";
import type { ChatState } from "./state.js";

export const showCreatedMaterial = (
    state: ChatState,
    command: TurnProgressed,
): readonly Signal[] => {
    const { event, conversationId } = command;
    if (event.type !== "material_build") return [];
    const { delta } = event;
    if (delta.type !== "build_settled") return [];
    if (state.selectedConversationId !== conversationId) return [];
    const alreadySettled = chatSessionFor(
        state.sessions,
        conversationId,
    ).conversation.some(
        (e) =>
            (e.type === "material_building" &&
                e.build.buildId === delta.buildId &&
                e.build.status === "settled") ||
            // The turn-end transcript reload replaced the live card with the
            // durable material entry, keyed `${buildId}-material`.
            (e.type === "material" && e.id === `${delta.buildId}-material`),
    );
    if (alreadySettled) return [];
    return [
        {
            _tag: "Signal.FrameRequested",
            transition: {
                type: "show-in-panel",
                ref: {
                    type: "material",
                    id: delta.materialId,
                    title: delta.title,
                },
            },
        },
    ];
};
